import {
  map, pick, find, first, words,
  sample, isArray, isString, isFinite,
  castArray, capitalize, isPlainObject
} from 'lodash-es'

import RE from './re'
import hosth from './hosth'
import envRaw from './env-raw'

import { getPostInfo, getMediaInfo } from './post-info'
import { isUrl, toUrl, isSrc, toSrc, escapeHtml, escapeHtmlVerbose } from './security'
import { isText, toText, isEmpty, defaultTo, isPosInteger, toPosInteger, isNotEmptyArray } from './bits'

import DEFAULT_CONTENT_ICON from '../../img/default-content.jpg'
import DEFAULT_AVATAR_ICON from '../../img/default-avatar.svg'
import INSTAGRAM_ANONYMOUS_ICON from '../../img/instagram-anonymous.png'

const DEFAULT_FILE = toSrc(DEFAULT_CONTENT_ICON)
const DEFAULT_AVATAR = toSrc(DEFAULT_AVATAR_ICON)
const INSTAGRAM_AVATAR = toSrc(INSTAGRAM_ANONYMOUS_ICON)

export default function fixPost(input) {
  const posts = castArray(input)
  const reports = []

  // order is important!
  fixMediaList(posts, reports)
  fixPreview(posts, reports)
  fixFields(posts, reports)
  fixTextEmojiToImg(posts, reports)
  fixTextPositionToHashtag(posts, reports)
  fixSecurityIssues(posts, reports)
  fixTwitterTextFull(posts, reports)
  fixTextFull(posts, reports)
  fixFacebookImageFileThumb(posts, reports)
  fixYoutubeVideoFileThumb(posts, reports)
  fixFiles(posts, reports)
  fixFileType(posts, reports)
  fixReferenceUrl(posts, reports)
  fixName(posts, reports)
  fixBrokenAvatar(posts, reports)
  fixSmallAvatar(posts, reports)
  fixUserAvatar(posts, reports)
  fixAvatars(posts, reports)
  fixExternalCreatedAt(posts, reports)
  fixVotesCount(posts, reports)

  printReports(posts, reports)

  return input
}

export function fixPostName(input) {
  // adapter for app/vue-dashboard/src/store/modules/galleryItems/dataProcessors.js

  const posts = castArray(input)
  const reports = []

  fixName(posts, reports)
  printReports(posts, reports)

  return input
}

export function fixPostReferenceUrl(input) {
  // adapter for app/vue-dashboard/src/store/modules/galleryItems/dataProcessors.js

  const posts = castArray(input)
  const reports = []

  fixReferenceUrl(posts, reports)
  printReports(posts, reports)

  return input
}

function printReports(posts, reports) {
  if (reports.length) {
    const id2data = reports.reduce((acc, { id, msg }) => {
      if (!acc[id]) {
        acc[id] = {}
        acc[id].post = posts.find((post) => post.id === id)
        acc[id].info = getPostInfo(acc[id].post)
        acc[id].messages = [msg]
      } else {
        acc[id].messages.push(msg)
      }
      return acc
    }, {})

    const reportText = '\n' + map(id2data, (data, id) => {
      const messages = data.messages
      const postInfo = data.info
      const textInfo = JSON.stringify(postInfo.shortInfo())
      return `\tpost with id=${id} as ${textInfo}: \n\t\t${messages.join('\n\t\t')}`
    }).join('\n')

    if (envRaw.showsFixPostDebug) {
      console.debug(`fixPost report: ${reportText}`)
    }
  }
}

// media_list

const textFields = [
  'text',
  'text_full',
  'text_emoji_to_img',
  'text_position_to_hashtag'
]

const fileFields = [
  'file',
  'file_thumb'
]

const commonFields = [
  'file_type',
  'content_type',
  'is_text_only',
  'external_id',
  'external_type'
]

function fixMediaList(posts, reports) {
  for (const post of posts) {
    const correct = true
      && isNotEmptyArray(post.media_list)
      && post.media_list.every(isPlainObject)

    let textMedia = false

    if (!correct) {
      reports.push({ id: post.id, msg: `fixMediaList: [media_list=${post.media_list}] has wrong format!` })
      const mediaInfo = getMediaInfo(post, post)
      if (mediaInfo.isSomeText) {
        post.media_list = castArray(pickTextAndCommonFields(post))
        textMedia = true
      } else {
        post.media_list = castArray(pickFileAndCommonFields(post))
      }
    }

    delFileFields(post)
    delCommonFields(post)
    if (textMedia) nullTextFields(post)

    for (let index = 0, length = post.media_list.length; index !== length; index++) {
      const media = post.media_list[index]
      const mediaInfo = getMediaInfo(post, media)
      if (mediaInfo.isSomeText) {
        post.media_list[index] = nullFileFields(pickTextAndCommonFields(media))
      } else {
        post.media_list[index] = nullTextFields(pickFileAndCommonFields(media))
      }
    }

    for (let index = 0, length = post.media_list.length; index !== length; index++) {
      const media = post.media_list[index]
      const media_index = defaultTo(toPosInteger(media.media_index), null)
      media.media_index = defaultTo(media_index, index)
    }

    post.media_list.sort(askDiffMedia)
  }
}

function askDiffMedia(a, b) {
  return a.media_index - b.media_index
}

function pickTextAndCommonFields(entry) {
  return pick(entry, ...textFields, ...commonFields)
}

function pickFileAndCommonFields(entry) {
  return pick(entry, ...fileFields, ...commonFields)
}

function delFileFields(entry) {
  for (const fileField of fileFields) {
    delete entry[fileField]
  }
  return entry
}

function delCommonFields(entry) {
  for (const commonField of commonFields) {
    delete entry[commonField]
  }
  return entry
}

function nullFileFields(entry) {
  for (const fileField of fileFields) {
    entry[fileField] = null
  }
  return entry
}

function nullTextFields(entry) {
  for (const textField of textFields) {
    entry[textField] = null
  }
  return entry
}

// fixPreview

function fixPreview(posts, _reports) {
  for (const post of posts) {
    if (post.preview) post.preview = find(post.media_list, post.preview)
    if (!post.preview) post.preview = first(post.media_list)
  }
}

// fields

function fixFields(posts, _reports) {
  for (const post of posts) {
    const type = defaultTo(toText(post.type), null)
    const entry_type = defaultTo(toText(post.entry_type), null)

    delete post.type
    post.entry_type = defaultTo(entry_type, type)

    for (const media of post.media_list) {
      const file_type = defaultTo(toText(media.file_type), null)
      const content_type = defaultTo(toText(media.content_type), null)

      delete media.content_type
      media.file_type = defaultTo(file_type, content_type)

      media.is_text_only = defaultTo(media.is_text_only, false)
    }
  }
}

// text_emoji_to_img

function fixTextEmojiToImg(entries, reports, post) {
  for (const entry of entries) {
    const required = false
      || isEmpty(post)
      || getMediaInfo(post, entry).isSomeText

    if (required) {
      const correct = isCorrectTextEmojiToImg(entry.text_emoji_to_img)
      if (correct) {
        entry.text_emoji_to_img = processTextEmojiToImg(entry.text_emoji_to_img)
      } else {
        entry.text_emoji_to_img = blankTextEmojiToImg()
      }
    }

    if (isEmpty(post)) {
      fixTextEmojiToImg(entry.media_list, reports, entry)
    }
  }
}

function isCorrectTextEmojiToImg(text_emoji_to_img) {
  return true
    && isPlainObject(text_emoji_to_img)
    && isArray(text_emoji_to_img.imgs)
    && isArray(text_emoji_to_img.emojis)
}

function processTextEmojiToImg(text_emoji_to_img) {
  return {
    imgs: text_emoji_to_img.imgs.map(toSrc),
    emojis: text_emoji_to_img.emojis.concat()
  }
}

function blankTextEmojiToImg() {
  return { imgs: [], emojis: [] }
}

// text_position_to_hashtag

function fixTextPositionToHashtag(entries, reports, post) {
  for (const entry of entries) {
    const required = false
      || isEmpty(post)
      || getMediaInfo(post, entry).isSomeText

    if (required) {
      const correct = isCorrectTextPositionToHashtag(entry.text_position_to_hashtag)
      if (correct) {
        entry.text_position_to_hashtag = processTextPositionToHashtag(entry.text_position_to_hashtag)
      } else {
        entry.text_position_to_hashtag = blankTextPositionToHashtag()
      }
    }

    if (isEmpty(post)) {
      fixTextPositionToHashtag(entry.media_list, reports, entry)
    }
  }
}

function isCorrectTextPositionToHashtag(text_position_to_hashtag) {
  return true
    && isPlainObject(text_position_to_hashtag)
    && isArray(text_position_to_hashtag.positions)
    && isArray(text_position_to_hashtag.hashtags)
}

function processTextPositionToHashtag(text_position_to_hashtag) {
  return {
    hashtags: text_position_to_hashtag.hashtags.concat(),
    positions: text_position_to_hashtag.positions.concat()
  }
}

function blankTextPositionToHashtag() {
  return { hashtags: [], positions: [] }
}

// security

function fixSecurityIssues(posts, _reports) {
  for (const post of posts) {
    // wipe unnecessary field
    post.text = null

    // convert html to text
    post.name = escapeHtml(post.name)
    post.reference = escapeHtml(post.reference)

    // strip "javascript:" protocol
    post.post_url = toUrl(post.post_url)
    post.reference_url = toUrl(post.reference_url)

    // sanitize text_full
    post.text_full = sanitizeTextFull(post.text_full, post.text_position_to_hashtag)

    // media_list
    for (const media of post.media_list) {
      const mediaInfo = getMediaInfo(post, media)
      if (mediaInfo.isSomeText) {
        media.text = null
        media.text_full = sanitizeTextFull(media.text_full, media.text_position_to_hashtag)
      }
    }
  }
}

function sanitizeTextFull(orig, map) {
  if (isEmpty(orig)) orig = ''

  const changes = []
  const text_full = escapeHtmlVerbose(orig, changes)

  const newHashtags = map.hashtags.map(escapeHtml)
  const oldPositions = map.positions
  const newPositions = map.positions.concat()

  for (let cIndex = 0, cLength = changes.length; cIndex !== cLength; cIndex++) {
    const change = changes[cIndex]
    const offset = change.replacer.length - change.match.length
    for (let pIndex = 0, pLength = oldPositions.length; pIndex !== pLength; pIndex++) {
      const oldPosition = oldPositions[pIndex]
      if (oldPosition > change.index) {
        newPositions[pIndex] += offset
      }
    }
  }

  map.hashtags = []
  map.positions = []

  for (let pIndex = 0, pLength = newPositions.length; pIndex !== pLength; pIndex++) {
    const hashtag = newHashtags[pIndex]
    const position = newPositions[pIndex]
    map.hashtags.push(hashtag)
    map.positions.push(position)
  }

  return text_full
}

// text_full

const twitterShareLinkRE = /(^|\s)https:\/\/t\.co\/[a-zA-Z0-9]+$/i

function fixTwitterTextFull(entries, reports, post) {
  for (const entry of entries) {
    const required = isEmpty(post)
      ? getPostInfo(entry).isTwitterHost
      : getMediaInfo(post, entry).isTwitterText

    if (required) {
      entry.text_full = toText(entry.text_full)
      if (isText(entry.text_full)) {
        entry.text_full = entry.text_full.replace(twitterShareLinkRE, '')
      }
    }

    if (isEmpty(post)) {
      fixTwitterTextFull(entry.media_list, reports, entry)
    }
  }
}

function fixTextFull(entries, reports, post) {
  for (const entry of entries) {
    const required = false
      || isEmpty(post)
      || getMediaInfo(post, entry).isSomeText

    if (required) {
      entry.text_full = toText(entry.text_full)
      const correct = isString(entry.text_full)

      if (!correct) {
        entry.text_full = ''
      }
    }

    if (isEmpty(post)) {
      fixTextFull(entry.media_list, reports, entry)
    }
  }
}

// file \ file_thumb

function fixFacebookImageFileThumb(posts, _reports) {
  for (const post of posts) {
    for (const media of post.media_list) {
      const mediaInfo = getMediaInfo(post, media)
      if (mediaInfo.isFacebookImage) {
        media.file = toSrc(media.file)
        media.file_thumb = toSrc(media.file_thumb)

        if (isSrc(media.file)) {
          media.file_thumb = media.file
        }
      }
    }
  }
}

const YT_THUMB_RE = /\/\w+\.\w+$/
const YT_BLACK_BAR_LESS_THUMB = '/mqdefault.jpg'

function fixYoutubeVideoFileThumb(posts, _reports) {
  for (const post of posts) {
    for (const media of post.media_list) {
      const mediaInfo = getMediaInfo(post, media)
      if (mediaInfo.isYoutubeVideo) {
        media.file = toSrc(media.file)
        media.file_thumb = toSrc(media.file_thumb)

        if (isSrc(media.file_thumb)) {
          media.file_thumb = media.file_thumb.replace(YT_THUMB_RE, YT_BLACK_BAR_LESS_THUMB)
        }
      }
    }
  }
}

function fixFiles(posts, reports) {
  for (const post of posts) {
    for (const media of post.media_list) {
      const mediaInfo = getMediaInfo(post, media)
      if (!mediaInfo.isSomeText) {
        media.file_thumb = toSrc(media.file_thumb)
        media.file = toSrc(media.file)

        if (!isSrc(media.file_thumb)) {
          reports.push({ id: post.id, msg: `fixFiles: [file_thumb=${media.file_thumb}] is empty!` })
          media.file_thumb = DEFAULT_FILE
        }

        if (!isSrc(media.file)) {
          reports.push({ id: post.id, msg: `fixFiles: [file=${media.file}] is empty!` })
          media.file = media.file_thumb
        }
      }
    }
  }
}

// file_type

const textTypeRE = /^text\//
const imageTypeRE = /^image\//
const videoTypeRE = /^video\//
const audioTypeRE = /^audio\//

const defaultTextType = 'text/plain'
const defaultImageType = 'image/jpeg'
const defaultVideoType = 'video/mp4'
const defaultAudioType = 'audio/mp3'

function fixFileType(posts, reports) {
  for (const post of posts) {
    for (const media of post.media_list) {
      const mediaInfo = getMediaInfo(post, media)
      if (!mediaInfo.isSomeText) {
        media.file_type = toText(media.file_type)
        if (mediaInfo.isSomeImage && !imageTypeRE.test(media.file_type)) {
          reports.push({ id: post.id, msg: `fixFileType: image [file_type=${media.file_type}] has wrong format!` })
          media.file_type = defaultImageType
        } else if (mediaInfo.isSomeVideo && !videoTypeRE.test(media.file_type)) {
          reports.push({ id: post.id, msg: `fixFileType: video [file_type=${media.file_type}] has wrong format!` })
          media.file_type = defaultVideoType
        } else if (mediaInfo.isSomeAudio && !audioTypeRE.test(media.file_type)) {
          reports.push({ id: post.id, msg: `fixFileType: audio [file_type=${media.file_type}] has wrong format!` })
          media.file_type = defaultAudioType
        } else if (mediaInfo.isSomeText && !textTypeRE.test(media.file_type)) {
          reports.push({ id: post.id, msg: `fixFileType: text [file_type=${media.file_type}] has wrong format!` })
          media.file_type = defaultTextType
        }
      }
    }
  }
}

// "reference_url" can be incorrect because this value is
// calculated base on "reference" and "entry_type" attributes
// and there are many of non facebook entries with "entry_type == 'facebook_media'"

function fixReferenceUrl(posts, reports) {
  for (const post of posts) {
    const postInfo = getPostInfo(post)

    post.reference = toText(post.reference)
    post.reference_url = toUrl(post.reference_url)

    if (postInfo.isUploadHost && (isUrl(post.reference_url) || isText(post.reference))) {
      reports.push({ id: post.id, msg: `fixReferenceUrl: [reference_url=${post.reference_url}] and [reference=${post.reference}] should be empty for upload post!` })
      post.reference_url = null
      post.reference = null
      continue
    } else if (postInfo.isUploadHost) {
      post.reference_url = null
      post.reference = null
      continue
    }

    if (!isText(post.reference)) {
      reports.push({ id: post.id, msg: `fixReferenceUrl: [reference=${post.reference}] is empty!` })
      post.reference = null
    }

    const checkReferenceUrl = hosth.getCheckReferenceUrl(postInfo)
    const checked = checkReferenceUrl && checkReferenceUrl(post.reference_url)
    const correct = isUrl(post.reference_url) && checked

    if (!correct) {
      reports.push({ id: post.id, msg: `fixReferenceUrl: [reference_url=${post.reference_url}] has wrong format!` })
      post.reference_url = null
    }

    if (isText(post.reference)) {
      const referenceToUrl = hosth.getReferenceToUrl(postInfo)
      const reference_url = true
        && referenceToUrl
        && referenceToUrl(post.reference)

      if (isUrl(reference_url)) {
        post.reference_url = reference_url
        reports.push({ id: post.id, msg: `fixReferenceUrl: [reference_url=${post.reference_url}] is rebuilt from [reference=${post.reference}]!` })
      } else {
        reports.push({ id: post.id, msg: `fixReferenceUrl: [reference_url=${post.reference_url}] cannot be rebuilt from [reference=${post.reference}]!` })
      }
    }
  }
}

// name

function fixName(posts, reports) {
  for (const post of posts) {
    const postInfo = getPostInfo(post)

    post.name = toText(post.name)
    post.reference = toText(post.reference)

    const defaultName = postInfo.isUploadHost
      ? toText('User')
      : toText(`${capitalize(postInfo.host)} User`)

    if (postInfo.isYoutubeHost || postInfo.isFacebookHost) {
      const badNameRE = postInfo.isYoutubeHost ? RE.LIKE_YB_HASH_REF : RE.IS_FB_NUM_REF
      const correct = isText(post.name) && !badNameRE.test(post.name)
      if (!correct) {
        reports.push({ id: post.id, msg: `fixName: [name=${post.name}] has wrong format!` })
        post.name = defaultName
      }
    } else if (isText(post.reference)) {
      post.name = post.reference
    } else if (!isText(post.name)) {
      reports.push({ id: post.id, msg: `fixName: [reference=${post.reference}] and [name=${post.name}] are both empty!` })
      post.name = defaultName
    }
  }
}

// avatars

const tumblrBrokenAvatarRE = /api\.tumblr\.com\/v\d+\/blog\/\/avatar/i
const facebookBrokenAvatarRE = /graph\.facebook\.com\/\/picture/i

function fixBrokenAvatar(posts, reports) {
  for (const post of posts) {
    const correct = true
      && !tumblrBrokenAvatarRE.test(post.user_avatar)
      && !facebookBrokenAvatarRE.test(post.user_avatar)

    if (!correct) {
      reports.push({ id: post.id, msg: `fixBrokenAvatar: [user_avatar=${post.user_avatar}] has wrong format!` })
      post.user_avatar = null
    }
  }
}

const facebookSmallAvatarRE = /graph\.facebook\.com\/.+\/picture\/?$/i
const facebookMediumSuffix = '?width=128&height=128'

function fixSmallAvatar(posts, _reports) {
  for (const post of posts) {
    const postInfo = getPostInfo(post)
    if (postInfo.isFacebookHost) {
      if (facebookSmallAvatarRE.test(post.user_avatar)) {
        post.user_avatar = post.user_avatar + facebookMediumSuffix
      }
    }
  }
}

function fixUserAvatar(posts, reports) {
  for (const post of posts) {
    post.user_avatar = toSrc(post.user_avatar)
    if (!isSrc(post.user_avatar)) {
      const postInfo = getPostInfo(post)
      const fallbackAvatar = toSrc(tryFallbackAvatar(post, postInfo))
      if (!isSrc(fallbackAvatar)) {
        reports.push({ id: post.id, msg: `fixUserAvatar: [user_avatar=${post.user_avatar}] has wrong format! fallback avatar is not found!` })
        post.user_avatar = null
      } else {
        post.user_avatar = fallbackAvatar
      }
    }
  }
}

function tryFallbackAvatar(post, postInfo) {
  if (isText(post.reference) && postInfo.isTumblrHost) {
    return 'https://api.tumblr.com/v2/blog/' + post.reference + '/avatar/128'
  } else if (isText(post.reference) && postInfo.isFacebookHost) {
    return 'https://graph.facebook.com/v18.0/' + post.reference + '/picture?width=128&height=128'
  } else if (isText(post.reference) && postInfo.isTwitterHost) {
    return 'https://twitter.com/' + post.reference + ' /profile_image?size=original'
  } else if (postInfo.isInstagramHost) {
    return INSTAGRAM_AVATAR
  } else if (isText(post.name)) {
    const chunks = words(post.name)
    const background = sample(['e74c3c', 'd35400', 'f39c12'])
    return 'https://ui-avatars.com/api/'
      + '?name=' + chunks.join('+')
      + '&length=' + chunks.length
      + '&background=' + background
      + '&color=ffffff&size=128&bold=true'
  } else {
    return null
  }
}

function fixAvatars(posts, reports) {
  for (const post of posts) {
    post.user_avatar = toSrc(post.user_avatar)
    post.default_avatar = toSrc(post.default_avatar)

    if (!isSrc(post.user_avatar)) {
      reports.push({ id: post.id, msg: `fixAvatars: [user_avatar=${post.user_avatar}] has wrong format!` })
      post.user_avatar = DEFAULT_AVATAR
    }

    if (!isSrc(post.default_avatar)) {
      post.default_avatar = DEFAULT_AVATAR
    }
  }
}

// other

function fixExternalCreatedAt(posts, reports) {
  for (const post of posts) {
    post.external_created_at = new Date(post.external_created_at).valueOf()
    const correct = isFinite(post.external_created_at)

    if (!correct) {
      reports.push({ id: post.id, msg: `fixExternalCreatedAt: [external_created_at=${post.external_created_at}] has wrong format!` })
      post.external_created_at = Date.now()
    }
  }
}

function fixVotesCount(posts, reports) {
  for (const post of posts) {
    post.votes_count = toPosInteger(post.votes_count)
    const correct = isPosInteger(post.votes_count)

    if (!correct) {
      reports.push({ id: post.id, msg: `fixVotesCount: [votes_count=${post.votes_count}] has wrong format!` })
      post.votes_count = 0
    }
  }
}
